import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getProductGroupList, ProductGroupItem } from "api/ProductGroup";
import { API_URL } from "env";

type ProductGroup = {
    id: number;
    name: string;
    totalProducts?: number;
    iconUrl: string;
    extraClassName?: string;
    title?: string;
    slug?: string;
};

const IMAGE_PLACEHOLDER =
    "data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2770%27%20height=%2770%27/%3e";

const imageWrapperStyle = {
    boxSizing: "border-box",
    display: "inline-block",
    overflow: "hidden",
    width: "initial",
    height: "initial",
    background: "none",
    opacity: 1,
    border: 0,
    margin: 0,
    padding: 0,
    position: "relative",
    maxWidth: "100%",
} as const;

const imageBaseStyle = {
    boxSizing: "border-box",
    display: "block",
    width: "initial",
    height: "initial",
    background: "none",
    opacity: 1,
    border: 0,
    margin: 0,
    padding: 0,
    maxWidth: "100%",
} as const;

const imageFillStyle = {
    position: "absolute",
    inset: 0,
    boxSizing: "border-box",
    padding: 0,
    border: "none",
    margin: "auto",
    display: "block",
    width: 0,
    height: 0,
    minWidth: "100%",
    maxWidth: "100%",
    minHeight: "100%",
    maxHeight: "100%",
} as const;

const getResponsiveClassName = (index: number) => {
    if (index >= 12) {
        return "styles_hiddenM__2qTuL styles_hiddenS__z_EOO styles_hiddenXs__lcVIm styles_hiddenXxs__s6z0d";
    }

    if (index >= 10) {
        return "styles_hiddenS__z_EOO styles_hiddenXs__lcVIm styles_hiddenXxs__s6z0d";
    }

    if (index >= 8) {
        return "styles_hiddenXs__lcVIm styles_hiddenXxs__s6z0d";
    }

    if (index >= 6) {
        return "styles_hiddenXxs__s6z0d";
    }

    return undefined;
};

const getProductGroupIconUrl = (avatar: string) => {
    if (!avatar) {
        return IMAGE_PLACEHOLDER;
    }

    if (avatar.startsWith("http")) {
        return avatar;
    }

    return `${API_URL}/${avatar.replace(/^\/+/, "")}`;
};

const mapProductGroup = (group: ProductGroupItem, index: number): ProductGroup => ({
    id: group.id,
    name: group.name,
    iconUrl: getProductGroupIconUrl(group.avatar),
    extraClassName: getResponsiveClassName(index),
    title: group.name,
    slug: group.slug,
});

const getDrugContainerClassName = (extraClassName?: string) => {
    const baseClassName = "MuiButtonBase-root styles_drugContainer__ObZ6i";
    return extraClassName ? `${baseClassName} ${extraClassName}` : baseClassName;
};

function ProductGroupButton({
    group,
    index,
}: {
    group: ProductGroup;
    index: number;
}) {
    const navigate = useNavigate();
    return (
        <button
            className={getDrugContainerClassName(group.extraClassName)}
            tabIndex={0}
            type="button"
            data-index={index}
            onClick={() => {
                navigate(`/products?group=${group.id}`);
            }}
        >
            <span style={imageWrapperStyle}>
                <span style={imageBaseStyle}>
                    <img
                        alt=""
                        aria-hidden="true"
                        src={IMAGE_PLACEHOLDER}
                        style={imageBaseStyle}
                    />
                </span>
                <img
                    alt="icon"
                    data-origin-url={group.iconUrl}
                    src={group.iconUrl}
                    decoding="async"
                    data-nimg="intrinsic"
                    style={imageFillStyle}
                />
                <noscript />
            </span>

            <div className="styles_wrapperInfo__u4SvA">
                <p
                    className="MuiTypography-root styles_name__mSTrZ MuiTypography-body1"
                    title={group.title}
                >
                    {group.name}
                </p>
                {typeof group.totalProducts === "number" && (
                    <p className="MuiTypography-root styles_totalProduct__Uve9g MuiTypography-body1">
                        {group.totalProducts} sản phẩm
                    </p>
                )}
            </div>

            <span className="MuiTouchRipple-root"></span>
        </button>
    );
}

export default function UuDaiHangHang() {
    const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);

    useEffect(() => {
        let isMounted = true;

        const fetchProductGroups = async () => {
            try {
                const items = await getProductGroupList();

                if (!isMounted) {
                    return;
                }

                setProductGroups(items.map(mapProductGroup));
            } catch (error) {
                console.error("Failed to fetch product groups", error);
            }
        };

        fetchProductGroups();

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <div className="!mt-6 index-NhomSP">
            <div className="styles_flexWrapper__no71p">
                <div id="list__drug" className="styles_borderRadius___plyA">
                    <div className="styles_parentContainer__AFmUF">
                        <p className="MuiTypography-root styles_title___NB_z MuiTypography-body1">
                            Nhóm sản phẩm
                            <button className="MuiButtonBase-root styles_viewAll__1if2W" tabIndex={0} type="button">
                                <span>Xem tất cả</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M16.006 11.0818C16.5418 11.5767 16.5418 12.4233 16.006 12.9182L11.5982 16.99C10.7976 17.7295 9.5 17.1617 9.5 16.0718V7.92819C9.5 6.83827 10.7976 6.27044 11.5982 7.01L16.006 11.0818Z"
                                        fill="#0E1983"
                                    ></path>
                                    <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" stroke="#0E1983"></rect>
                                </svg>
                                <span className="MuiTouchRipple-root"></span>
                            </button>
                        </p>

                        <div className="styles_drugWrapper__ty7XO">
                            {productGroups.map((group, index) => (
                                <ProductGroupButton key={group.id} group={group} index={index} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}